
























































import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { User } from '@/api/model/user'
import { Referral } from '@/api/model/referral'
import { getAllowedUsers } from '@/helpers/security'
import { Comment } from '@/api/model/comment'
import { cloneDeep } from 'lodash'
import { v4 as uuid } from 'uuid'
import { CommentOrigin, CommentVisibility } from '@/api/types'
import { CommentAPI, MentionAPI } from '@/api/backend-client'
import CommentBlock from '@/components/comment/CommentBlock.vue'
import LoaderBalls from '@/components/LoaderBalls.vue'
import IconVisibility from '@/components/icons/IconVisibility.vue'
import InputSelect from '@/components/input/InputSelect.vue'
import InputMentionable from '@/components/input/InputMentionable.vue'
import KvButton from '@/components/input/KvButton.vue'

@Component({
    components: {
        KvButton,
        InputMentionable,
        InputSelect,
        IconVisibility,
        LoaderBalls,
        CommentBlock,
    },
})
export default class CommentList extends Vue {
    @Prop({ required: true }) readonly referral!: Referral
    comments: Comment[] = []
    mentionableUsers: User[] = []
    loading: boolean = false
    newComment: string = ''
    newCommentVisibility: CommentVisibility = null
    newCommentSaving: boolean = false

    mounted() {
        this.comments = cloneDeep(this.referral.comments)
        this.loadContent()
        this.resetNewComment()
    }

    async loadContent(): Promise<void> {
        this.loading = true
        await this.loadMentionableUsers()
        this.loading = false
    }

    resetNewComment(): void {
        this.newComment = ''
        this.newCommentVisibility = this.$currentCompany.defaultCommentVisibility
    }

    async loadMentionableUsers(): Promise<void> {
        this.mentionableUsers = await getAllowedUsers(
            this.referral.referredByBranch,
            this.referral.referredBy,
            this.referral.assignedToBranch,
            this.referral.assignedTo,
        )

        this.mentionableUsers = this.mentionableUsers.filter(user => user.active)
    }

    async addNewComment(): Promise<void> {
        try {
            this.newCommentSaving = true
            const comment = await CommentAPI.create(Comment.fromData({
                referral: this.referral.resourceIdentifier,
                user: this.$currentUser.resourceIdentifier,
                id: uuid(),
                comment: this.newComment,
                origin: CommentOrigin.USER,
                visibility: this.newCommentVisibility,
            }))

            const mentionedUsers = this.mentionableUsers
                .filter(user => comment.comment.indexOf(`data-id="${user.id}"`) >= 0)

            if (mentionedUsers.length > 0) {
                await MentionAPI.mention(
                    comment,
                    mentionedUsers,
                )
            }

            this.comments.push(comment)
            this.resetNewComment()

        } catch (error) {
            this.$notify({
                type: 'error',
                title: 'Foutmelding',
                text: error.response.data['hydra:description'] ?? error.message,
            })

        } finally {
            this.newCommentSaving = false
        }
    }

    async onCommentSaved(comment: Comment): Promise<void> {
        this.comments.splice(this.comments.findIndex(v => v.id === comment.id), 1)
        this.comments.push(comment)
    }

    async onCommentDeleted(comment: Comment): Promise<void> {
        this.comments.splice(this.comments.findIndex(v => v.id === comment.id), 1)
    }

    @Watch('comments')
    onCommentsChanged(): void {
        this.$emit('commentsChanged', this.comments)
    }

    visibilityLabel(visibility: string): string {
        return this.$t(`comment.visibility.${visibility}`, {
            userName: this.$currentUser.name,
            companyName: this.$currentCompany.name,

        }).toString()
    }

    get sortedComments(): Comment[] {
        return this.comments
            .filter(comment => comment instanceof Comment)
            .sort((a: Comment, b: Comment) => a.createdAt > b.createdAt ? -1 : 1)
    }

    get visibilityOptions() {
        return ['personal', 'private', 'public'].map(visibility => ({
            label: this.visibilityLabel(visibility),
            value: visibility,
        }))
    }
}
